import React, {FC, ReactNode, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf, RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  EmojiDefinitionAndSkinTone,
  emojiToDefinition,
  unifiedToShortcode,
} from '../../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {CommentReactionVisualProps} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {getEmojiValidationMap} from '../../../../../store/stateless/emojiSelectors';
import {EmojiDropdownContainer} from '../../../../common/dropdowns/emoji/emojiDropdownContainer';
import {Emoji} from '../../../../common/emoji/emoji';
import {CommentEmojiReactionTooltip} from './commentEmojiReactionTooltip';

/*
 * Props.
 */

export interface CommentReactionRendererProps {
  isUserSelected?: boolean;
  isDisabled?: boolean;
  onClick: Handler;
  renderReactionNode: (emojiSize: number) => ReactNode;
}

export interface CommentReactionsProps {
  isAddButtonOnly?: boolean;
  canAddReactions?: boolean;
  reactions?: Array<CommentReactionVisualProps>;
  userId?: number;
  renderCommentReaction?: RendererOf<CommentReactionRendererProps>;
  onToggleReaction?: HandlerOf<string>;
  onAddReaction?: HandlerOf<string>;
  onTogglePicker?: Handler;
}

/*
 * Style.
 */

interface CommentReactionButtonStyleProps {
  $isUserSelected?: boolean;
  $isDisabled?: boolean;
  $isActive?: boolean;
  $isIconOnly?: boolean;
}

export const StyledReactionButton = styled(ChromelessButton)<CommentReactionButtonStyleProps>`
  background: ${(p) => p.theme.greys.shade30};
  height: 24px;
  border-radius: 12px;
  padding: 4px 8px 4px 6px;
  display: grid;
  grid-gap: 4px;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;

  margin-right: 4px;
  margin-bottom: 4px;

  ${(p) => maybeAddHoverStyle(p)};

  ${(p) => addSelectedStyles(p)};
`;

const StyledAddReactionButton = styled(StyledReactionButton)`
  ${(p) =>
    !p.$isIconOnly &&
    css`
      padding: 3px 9px 5px 11px;
    `};
`;

function maybeAddHoverStyle({$isUserSelected, $isDisabled}: CommentReactionButtonStyleProps) {
  if ($isDisabled) {
    return '';
  }

  if ($isUserSelected) {
    return css`
      &:hover {
        background: ${(p) => p.theme.message.commentReactionHoverBackground};
      }
    `;
  }

  return css`
    &:hover {
      background: ${(p) => p.theme.greys.shade40};
    }
  `;
}

function addSelectedStyles({$isUserSelected, $isActive, $isIconOnly}: CommentReactionButtonStyleProps) {
  if ($isIconOnly) {
    return css`
      height: 30px;
      background: ${$isActive ? 'alphas.gray10' : 'transparent'};
      padding: 7px;
      margin-bottom: 0px;
      margin-right: -4px;
      border-radius: 50%;

      &:hover {
        background: ${(p) => p.theme.alphas.gray10};
      }
    `;
  }

  if ($isActive) {
    return css`
      background: ${(p) => p.theme.greys.shade40};
    `;
  }

  if (!$isUserSelected) {
    return css`
      color: ${(p) => p.theme.greys.shade80};
    `;
  }

  return css`
    background: ${(p) => p.theme.message.commentReactionBackground};
    color: ${(p) => p.theme.palette.blue.shade50};
  `;
}

const StyledReactionCountDiv = styled.div`
  color: currentColor;
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.tiny};
  line-height: 16px;
  font-variant-numeric: tabular-nums;
`;

/*
 * Component.
 */

export const CommentReactions: FC<CommentReactionsProps> = (props) => {
  const theme = useUserTheme();
  const addReactionRef = useRef<HTMLDivElement>(null);
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const emojiValidationMap = useAppSelector(getEmojiValidationMap);

  const onAddClick = () => {
    setIsPickerVisible(true);

    if (props.onTogglePicker) {
      props.onTogglePicker();
    }
  };

  const onRequestClose = () => {
    setIsPickerVisible(false);

    if (props.onTogglePicker) {
      props.onTogglePicker();
    }
  };

  const onEmojiSelect: HandlerOf<string> = (shortcode) => {
    setIsPickerVisible(false);

    if (props.onTogglePicker) {
      props.onTogglePicker();
    }

    if (props.onAddReaction) {
      props.onAddReaction(shortcode);
    }
  };

  const onToggleEmoji: HandlerOf<string> = (shortcode) => {
    if (props.onToggleReaction) {
      props.onToggleReaction(shortcode);
    }
  };

  const commentReactionRenderer = props.renderCommentReaction || defaultCommentReactionRenderer;

  return (
    <>
      {props.reactions &&
        props.reactions.map((reaction) => {
          const emoji = emojiToDefinition(reaction.emojiString, emojiValidationMap);
          const shortcode = unifiedToShortcode(reaction.emojiString);

          return (
            <TooltipCoordinator
              key={reaction.emojiString}
              render={() => (
                <CommentEmojiReactionTooltip
                  teammates={reaction.teammates}
                  shortcode={shortcode}
                  userId={props.userId}
                />
              )}
            >
              {commentReactionRenderer({
                isUserSelected: reaction.isUserSelected,
                isDisabled: !props.canAddReactions,
                onClick: () => onToggleEmoji(shortcode),
                renderReactionNode: (emojiSize: number) => (
                  <>
                    <div>{renderEmojiOrShortcode(emoji, shortcode, emojiSize)}</div>
                    <StyledReactionCountDiv>{reaction.teammates.length}</StyledReactionCountDiv>
                  </>
                ),
              })}
            </TooltipCoordinator>
          );
        })}
      {props.canAddReactions && (
        <TooltipCoordinator render={() => <Trans id="comment-reactions-react">React</Trans>}>
          <StyledAddReactionButton
            $isActive={isPickerVisible}
            $isIconOnly={props.isAddButtonOnly}
            ref={addReactionRef}
            onClick={onAddClick}
          >
            <Icon
              name="addReactionSmall"
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
              hasHover={isPickerVisible}
            />
          </StyledAddReactionButton>
        </TooltipCoordinator>
      )}
      {isPickerVisible && (
        <EmojiDropdownContainer
          anchor={addReactionRef}
          onRequestClose={onRequestClose}
          onEmojiSelect={onEmojiSelect}
        />
      )}
    </>
  );
};

function renderEmojiOrShortcode(
  emoji: EmojiDefinitionAndSkinTone | undefined,
  shortcode: string,
  emojiSize: number,
) {
  if (!emoji) {
    return <span>{shortcode}</span>;
  }
  return <Emoji size={emojiSize} emoji={emoji.emoji} skinTone={emoji.skinTone} />;
}

const defaultCommentReactionRenderer: RendererOf<CommentReactionRendererProps> = (props) => (
  <StyledReactionButton
    $isUserSelected={props.isUserSelected}
    $isDisabled={props.isDisabled}
    isDisabled={props.isDisabled}
    onClick={props.onClick}
  >
    {props.renderReactionNode(16)}
  </StyledReactionButton>
);
